import { useEffect, useMemo, useState } from "react";
import {
  Plus, Pencil, Trash2, X, Send, Phone, Mail, MessageCircle, Bot,
  Calendar as CalIcon, Clock, AlertTriangle, Power, PowerOff,
  Link as LinkIcon, Copy, Check, Sparkles, Unlink,
} from "lucide-react";
import { api, cx, formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";

const ROLES = ["cleaner", "driver", "concierge", "chef", "captain", "host", "security", "other"];
const TRIGGERS = ["before_start", "before_end", "on_request", "on_completion"];
const CHANNELS = ["telegram", "whatsapp", "email"];

const blankMember = {
  name: "", role: "cleaner", email: "", whatsapp: "", telegram: "",
  languages: "", notes: "", is_active: true,
};

const blankRule = {
  title: "",
  team_member_id: "",
  service_id: "",
  trigger: "before_start",
  offset_minutes: 1440,
  channel: "telegram",
  template: "Hi {{guest}}, tomorrow at 13:00 service required for {{service}}. Please confirm.",
  requires_confirmation: true,
  is_active: true,
};

function fmtOffset(min) {
  if (min == null) return "—";
  if (min === 0) return "0";
  if (min % 1440 === 0) return `${min / 1440}d`;
  if (min % 60 === 0) return `${min / 60}h`;
  return `${min}m`;
}

/** Provider Team & Automation tab — manage members, rules, mock dispatch log. */
export default function TeamSection({ services }) {
  const { t } = useI18n();
  const [members, setMembers] = useState([]);
  const [rules, setRules] = useState([]);
  const [preview, setPreview] = useState([]);
  const [log, setLog] = useState([]);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [memberModal, setMemberModal] = useState(null);  // null | {mode, data}
  const [ruleModal, setRuleModal] = useState(null);
  const [inviteModal, setInviteModal] = useState(null);  // {member, code, link, expires_at}
  const [seededOnce, setSeededOnce] = useState(false);

  const reload = async () => {
    setError(null);
    try {
      const [m, r, p, l] = await Promise.all([
        api.get("/providers/me/team"),
        api.get("/providers/me/workflows"),
        api.get("/providers/me/automation/preview").catch(() => ({ data: { items: [] } })),
        api.get("/providers/me/automation/messages").catch(() => ({ data: { items: [] } })),
      ]);
      setMembers(m.data.items || []);
      setRules(r.data.items || []);
      setPreview(p.data.items || []);
      setLog(l.data.items || []);
    } catch (e) { setError(formatErr(e)); }
  };
  useEffect(() => { reload(); }, []);

  // Auto-seed 3 starter rules the first time the provider has ≥1 active member
  // and no rules at all. Idempotent on the backend, so safe to retry.
  useEffect(() => {
    if (seededOnce) return;
    if (!members.length || rules.length) return;
    setSeededOnce(true);
    api.post("/providers/me/automation/seed-starter-rules", {})
      .then(() => reload())
      .catch(() => {/* non-fatal */});
  }, [members.length, rules.length, seededOnce]);

  const flashMsg = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), 2800);
  };

  // --- members ---------------------------------------------------------
  const saveMember = async (data, id) => {
    setError(null);
    const payload = {
      ...data,
      languages: typeof data.languages === "string"
        ? data.languages.split(",").map((s) => s.trim()).filter(Boolean)
        : (data.languages || []),
      email: data.email || null,
      whatsapp: data.whatsapp || null,
      telegram: data.telegram || null,
      notes: data.notes || null,
    };
    try {
      if (id) {
        await api.patch(`/providers/me/team/${id}`, payload);
        flashMsg(t("provider_team.member_updated", "Team member updated."));
      } else {
        await api.post(`/providers/me/team`, payload);
        flashMsg(t("provider_team.member_added", "Team member added."));
      }
      setMemberModal(null);
      await reload();
    } catch (e) { setError(formatErr(e)); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(t("provider_team.remove_confirm", "Remove this team member from your roster?"))) return;
    try {
      await api.delete(`/providers/me/team/${m.id}`);
      flashMsg(t("provider_team.member_removed", "Team member removed."));
      await reload();
    } catch (e) { setError(formatErr(e)); }
  };

  const issueInvite = async (m) => {
    setError(null);
    try {
      const { data } = await api.post(`/providers/me/team/${m.id}/invite`, {});
      setInviteModal({ member: m, ...data });
    } catch (e) { setError(formatErr(e)); }
  };

  const unlinkTelegram = async (m) => {
    if (!window.confirm(t("provider_team.unlink_confirm", "Unlink Telegram for this team member?"))) return;
    try {
      await api.delete(`/providers/me/team/${m.id}/telegram`);
      flashMsg(t("provider_team.unlinked", "Telegram unlinked."));
      await reload();
    } catch (e) { setError(formatErr(e)); }
  };

  // --- rules -----------------------------------------------------------
  const saveRule = async (data, id) => {
    setError(null);
    const payload = {
      ...data,
      offset_minutes: Number(data.offset_minutes || 0),
      service_id: data.service_id || null,
    };
    try {
      if (id) {
        await api.patch(`/providers/me/workflows/${id}`, payload);
        flashMsg(t("provider_team.rule_updated", "Rule updated."));
      } else {
        await api.post(`/providers/me/workflows`, payload);
        flashMsg(t("provider_team.rule_added", "Rule added."));
      }
      setRuleModal(null);
      await reload();
    } catch (e) { setError(formatErr(e)); }
  };

  const toggleRule = async (r) => {
    try {
      await api.patch(`/providers/me/workflows/${r.id}`, { is_active: !r.is_active });
      await reload();
    } catch (e) { setError(formatErr(e)); }
  };

  const removeRule = async (r) => {
    if (!window.confirm(t("provider_team.remove_confirm", "Remove this entry?"))) return;
    try {
      await api.delete(`/providers/me/workflows/${r.id}`);
      flashMsg(t("provider_team.rule_removed", "Rule removed."));
      await reload();
    } catch (e) { setError(formatErr(e)); }
  };

  const testSend = async (r) => {
    try {
      await api.post(`/providers/me/automation/test-send`, {
        rule_id: r.id,
        team_member_id: r.team_member_id,
        channel: r.channel,
        body: r.template,
      });
      flashMsg(t("provider_team.test_send_sent", "Test sent (mock — channel not yet wired)."));
      await reload();
    } catch (e) { setError(formatErr(e)); }
  };

  const memberById = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m])), [members]);

  return (
    <div data-testid="prov-team-tab" className="space-y-8">
      <div className="luxe-card p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="eyebrow">{t("provider_dash.tab_team", "Team")}</div>
            <h2 className="font-serif-display text-3xl mt-2">
              {t("provider_team.section_title", "Team & automations")}
            </h2>
            <p className="text-sm text-muted mt-2 max-w-2xl">
              {t("provider_team.section_subtitle", "Add team members (cleaners, drivers, hosts) and let our AI agent handle reminders, confirmations and status updates on your behalf.")}
            </p>
          </div>
        </div>
        <div className="mt-4 luxe-card p-3 text-xs text-teal/85 border-teal/30 bg-teal/5" data-testid="team-channel-banner">
          <Bot size={12} className="inline-block mr-1.5 -mt-0.5" />
          {t("provider_team.channel_live_banner", "Telegram delivery is live. Invite a team member to link their account, then automation rules dispatch automatically — no extra setup needed.")}
        </div>
      </div>

      {error && <div className="text-rose-300 text-sm" data-testid="team-error">{error}</div>}
      {flash && <div className="luxe-card p-3 text-sm text-teal border-teal/40" data-testid="team-flash">{flash}</div>}

      {/* Members */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-2xl">{t("provider_dash.tab_team", "Team")}</h3>
          <button
            onClick={() => setMemberModal({ mode: "create", data: blankMember })}
            className="btn btn-primary"
            data-testid="btn-add-team-member"
          >
            <Plus size={16} /> {t("provider_team.add_member", "Add team member")}
          </button>
        </div>
        {members.length === 0 ? (
          <div className="luxe-card p-10 text-center text-muted text-sm">
            <div className="font-serif-display text-2xl text-bone mb-2">
              {t("provider_team.no_members_title", "No team members yet.")}
            </div>
            {t("provider_team.no_members_body", "Add cleaners, drivers, hosts or chefs and we'll keep them in sync with each booking — automatically.")}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {members.map((m) => (
              <MemberCard
                key={m.id}
                m={m}
                t={t}
                onEdit={() => setMemberModal({ mode: "edit", id: m.id, data: { ...m, languages: (m.languages || []).join(", ") } })}
                onRemove={() => removeMember(m)}
                onInvite={() => issueInvite(m)}
                onUnlink={() => unlinkTelegram(m)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Rules */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-serif-display text-2xl">{t("provider_team.workflows_title", "Automation rules")}</h3>
            <p className="text-xs text-muted mt-1">{t("provider_team.workflows_subtitle", "Trigger reminders and AI-led check-ins automatically.")}</p>
          </div>
          <button
            onClick={() => setRuleModal({ mode: "create", data: { ...blankRule, team_member_id: members[0]?.id || "" } })}
            disabled={members.length === 0}
            className={cx("btn btn-primary", members.length === 0 && "opacity-50")}
            data-testid="btn-add-workflow"
          >
            <Plus size={16} /> {t("provider_team.add_rule", "Add rule")}
          </button>
        </div>
        {rules.length === 0 ? (
          <div className="luxe-card p-10 text-center text-muted text-sm">
            <div className="font-serif-display text-2xl text-bone mb-2">
              {t("provider_team.no_rules_title", "No automation rules yet.")}
            </div>
            {t("provider_team.no_rules_body", "Add a rule and we'll ping the right team member at the right time. Confirmations get logged here too.")}
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map((r) => (
              <RuleRow key={r.id} r={r} t={t}
                onEdit={() => setRuleModal({ mode: "edit", id: r.id, data: { ...r, service_id: r.service_id || "" } })}
                onRemove={() => removeRule(r)}
                onToggle={() => toggleRule(r)}
                onTestSend={() => testSend(r)}
                memberById={memberById}
              />
            ))}
          </div>
        )}
      </section>

      {/* Preview + Log */}
      <section className="grid lg:grid-cols-2 gap-6">
        <div data-testid="team-preview-block">
          <h3 className="font-serif-display text-xl mb-1">{t("provider_team.preview_title", "Upcoming reminders")}</h3>
          <p className="text-xs text-muted mb-3">{t("provider_team.preview_subtitle", "What we'd dispatch given current bookings + active rules.")}</p>
          {preview.length === 0 ? (
            <div className="luxe-card p-6 text-sm text-muted">{t("provider_team.preview_empty", "No reminders queued yet — add a rule and a booking to see it appear.")}</div>
          ) : (
            <ul className="space-y-2">
              {preview.slice(0, 8).map((p, i) => (
                <li key={i} className="luxe-card p-3" data-testid={`team-preview-${i}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="chip text-[10px]">{t(`provider_team.channel_${p.channel}`, p.channel)}</span>
                    <span className="chip success text-[10px]"><Bot size={10} /> {p.team_member_name}</span>
                    <span className="text-[11px] text-muted font-mono">{t("provider_team.preview_fire_at", "Fires at")} {p.fire_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-sm font-serif-display truncate">{p.service_title} · {p.booking_dates}</div>
                  <div className="text-[11px] text-muted mt-1 line-clamp-2">{p.preview}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div data-testid="team-log-block">
          <h3 className="font-serif-display text-xl mb-1">{t("provider_team.log_title", "Recent automation messages")}</h3>
          <p className="text-xs text-muted mb-3">&nbsp;</p>
          {log.length === 0 ? (
            <div className="luxe-card p-6 text-sm text-muted">{t("provider_team.log_empty", "Nothing dispatched yet — Phase A persists everything as 'mock' until channels are wired.")}</div>
          ) : (
            <ul className="space-y-2">
              {log.slice(0, 8).map((m) => (
                <li key={m.id} className="luxe-card p-3" data-testid={`team-log-${m.id}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={cx("chip text-[10px]", m.status === "sent" ? "success" : m.status === "failed" ? "danger" : "")}>
                      {t(`provider_team.log_status_${m.status}`, m.status)}
                    </span>
                    <span className="chip text-[10px]">{t(`provider_team.channel_${m.channel}`, m.channel)}</span>
                    <span className="text-[11px] text-muted font-mono">{m.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-sm">{m.team_member_name || "—"}</div>
                  <div className="text-[11px] text-muted mt-1 line-clamp-2">{m.body}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {memberModal && (
        <MemberModal
          state={memberModal}
          onClose={() => setMemberModal(null)}
          onSave={saveMember}
          t={t}
        />
      )}
      {ruleModal && (
        <RuleModal
          state={ruleModal}
          onClose={() => setRuleModal(null)}
          onSave={saveRule}
          members={members}
          services={services || []}
          t={t}
        />
      )}
      {inviteModal && (
        <InviteModal
          state={inviteModal}
          onClose={() => setInviteModal(null)}
          onRevoke={async () => {
            try {
              await api.delete(`/providers/me/team/${inviteModal.member.id}/invite`);
              setInviteModal(null);
              flashMsg(t("provider_team.invite_revoked", "Invite revoked."));
            } catch (e) { setError(formatErr(e)); }
          }}
          t={t}
        />
      )}
    </div>
  );
}

function MemberCard({ m, t, onEdit, onRemove, onInvite, onUnlink }) {
  const linked = !!m.telegram_chat_id;
  return (
    <div className={cx("luxe-card p-4 space-y-3", !m.is_active && "opacity-60")} data-testid={`tm-card-${m.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-serif-display text-lg truncate">{m.name}</div>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="chip text-[10px]">{t(`provider_team.role_${m.role}`, m.role)}</span>
            {linked ? (
              <span className="chip success text-[10px]" data-testid={`tm-tg-linked-${m.id}`}><Check size={10} /> {t("provider_team.tg_linked", "Telegram linked")}</span>
            ) : (
              <span className="chip warning text-[10px]" data-testid={`tm-tg-pending-${m.id}`}>{t("provider_team.tg_pending", "Not linked")}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} className="btn btn-ghost text-[11px] !px-1.5 !py-1" data-testid={`tm-edit-${m.id}`} aria-label="Edit">
            <Pencil size={12} />
          </button>
          <button onClick={onRemove} className="btn btn-ghost text-[11px] !px-1.5 !py-1 text-rose-300" data-testid={`tm-remove-${m.id}`} aria-label="Remove">
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      <ul className="space-y-1.5 text-xs text-muted">
        {m.email && <li className="inline-flex items-center gap-1.5"><Mail size={11} /> {m.email}</li>}
        {m.whatsapp && <li className="inline-flex items-center gap-1.5"><MessageCircle size={11} /> {m.whatsapp}</li>}
        {m.telegram && <li className="inline-flex items-center gap-1.5"><Send size={11} /> {m.telegram}</li>}
        {linked && m.telegram_first_name && (
          <li className="inline-flex items-center gap-1.5 text-teal/80">
            <Bot size={11} /> {t("provider_team.tg_chat_as", "Linked as {{name}}", { name: `${m.telegram_first_name}${m.telegram_username ? ` (@${m.telegram_username})` : ""}` })}
          </li>
        )}
        {!m.is_active && <li className="text-rose-200/80">{t("provider_team.rule_inactive", "Inactive")}</li>}
      </ul>
      {(m.languages || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {m.languages.map((l) => <span key={l} className="chip text-[10px]">{l}</span>)}
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-1 border-t hairline">
        {linked ? (
          <button onClick={onUnlink} className="btn btn-ghost text-[11px] !px-2 !py-1 text-rose-300" data-testid={`tm-unlink-${m.id}`}>
            <Unlink size={11} /> {t("provider_team.unlink_telegram", "Unlink Telegram")}
          </button>
        ) : (
          <button onClick={onInvite} className="btn btn-outline text-[11px] !px-2 !py-1" data-testid={`tm-invite-${m.id}`}>
            <LinkIcon size={11} /> {t("provider_team.invite_via_telegram", "Invite via Telegram")}
          </button>
        )}
      </div>
    </div>
  );
}

function InviteModal({ state, onClose, onRevoke, t }) {
  const [copied, setCopied] = useState(false);
  const expiresOn = state.expires_at ? new Date(state.expires_at) : null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(state.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {/*ignore*/}
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6 bg-ink-deep/70 backdrop-blur-sm" data-testid="invite-modal">
      <div className="w-full md:max-w-lg max-h-[95svh] overflow-y-auto modal-panel">
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b hairline glass-solid z-10">
          <div>
            <div className="eyebrow">{t("provider_team.tg_linked", "Telegram link")}</div>
            <div className="font-serif-display text-xl">{t("provider_team.invite_title", "Invite {{name}}", { name: state.member?.name })}</div>
          </div>
          <button onClick={onClose} className="text-muted hover:text-bone" data-testid="invite-modal-close" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-sm text-muted">
            {t("provider_team.invite_body", "Share this private link with {{name}}. They tap it on their phone — Telegram opens, they press Start, and the bot links their account so we can ping them automatically.", { name: state.member?.name })}
          </p>
          <div className="luxe-card p-3 flex items-center gap-2" data-testid="invite-link-box">
            <Sparkles size={14} className="text-teal shrink-0" />
            <code className="text-xs flex-1 truncate text-bone font-mono">{state.link}</code>
            <button onClick={copy} className="btn btn-primary text-[11px] !px-2 !py-1.5 shrink-0" data-testid="invite-copy">
              {copied ? <><Check size={12} /> {t("provider_team.invite_copied", "Copied")}</> : <><Copy size={12} /> {t("provider_team.invite_copy", "Copy")}</>}
            </button>
          </div>
          <div className="flex flex-wrap gap-2 text-[11px] text-muted/80">
            <span className="chip">{t("provider_team.invite_expires", "Expires {{when}}", { when: expiresOn ? expiresOn.toLocaleDateString() : "—" })}</span>
            <a href={state.link} target="_blank" rel="noreferrer" className="chip text-teal hover:bg-teal/15">
              {t("provider_team.invite_open", "Open Telegram now")}
            </a>
          </div>
          <ol className="text-xs text-muted/80 space-y-1.5 list-decimal pl-5">
            <li>{t("provider_team.invite_step_1", "Send the link via SMS, WhatsApp, email — anything they can tap on their phone.")}</li>
            <li>{t("provider_team.invite_step_2", "They tap Start in Telegram. The bot acknowledges and they're linked.")}</li>
            <li>{t("provider_team.invite_step_3", "The card on this page flips to 'Telegram linked' and reminders start flowing.")}</li>
          </ol>
          <div className="flex justify-between gap-3 pt-2">
            <button onClick={onRevoke} className="btn btn-ghost text-[12px] text-rose-300" data-testid="invite-revoke">
              {t("provider_team.invite_revoke", "Revoke link")}
            </button>
            <button onClick={onClose} className="btn btn-primary text-[12px]" data-testid="invite-done">
              {t("provider_team.invite_done", "Done")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RuleRow({ r, t, onEdit, onRemove, onToggle, onTestSend, memberById }) {
  const m = memberById[r.team_member_id];
  return (
    <div className={cx("luxe-card p-4 md:p-5 grid gap-3 md:grid-cols-[1fr,auto] items-start", !r.is_active && "opacity-60")} data-testid={`rule-${r.id}`}>
      <div>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          {r.is_active ? (
            <span className="chip success text-[10px]">{t("provider_team.channel_" + r.channel, r.channel)}</span>
          ) : (
            <span className="chip text-[10px]">{t("provider_team.rule_inactive", "Inactive")}</span>
          )}
          <span className="chip text-[10px]"><Clock size={10} /> {t(`provider_team.trigger_${r.trigger}`, r.trigger)} · {fmtOffset(r.offset_minutes)}</span>
          {r.service_title ? (
            <span className="chip text-[10px]">{r.service_title}</span>
          ) : (
            <span className="chip text-[10px]">{t("provider_team.rule_service_all", "All services")}</span>
          )}
        </div>
        <div className="font-serif-display text-lg">{r.title}</div>
        <div className="text-xs text-muted mt-1">→ {m?.name || r.team_member_name || "—"} ({m?.role || r.team_member_role || "—"})</div>
        <div className="text-xs text-muted mt-2 italic line-clamp-2">"{r.template}"</div>
      </div>
      <div className="flex flex-wrap gap-2 md:flex-col md:items-end">
        <button onClick={onTestSend} className="btn btn-outline text-[11px] !px-2 !py-1" data-testid={`rule-test-${r.id}`}>
          <Send size={11} /> {t("provider_team.test_send_btn", "Send test message")}
        </button>
        <div className="flex gap-1">
          <button onClick={onToggle} className="btn btn-ghost text-[11px] !px-1.5 !py-1" data-testid={`rule-toggle-${r.id}`} aria-label="Toggle">
            {r.is_active ? <Power size={12} /> : <PowerOff size={12} />}
          </button>
          <button onClick={onEdit} className="btn btn-ghost text-[11px] !px-1.5 !py-1" data-testid={`rule-edit-${r.id}`} aria-label="Edit">
            <Pencil size={12} />
          </button>
          <button onClick={onRemove} className="btn btn-ghost text-[11px] !px-1.5 !py-1 text-rose-300" data-testid={`rule-remove-${r.id}`} aria-label="Remove">
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

function MemberModal({ state, onClose, onSave, t }) {
  const [data, setData] = useState(state.data);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const isEdit = state.mode === "edit";
  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6 bg-ink-deep/70 backdrop-blur-sm" data-testid="member-modal">
      <div className="w-full md:max-w-2xl max-h-[95svh] overflow-y-auto modal-panel">
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b hairline glass-solid z-10">
          <div className="font-serif-display text-xl">
            {isEdit ? t("provider_team.edit_member_title", "Edit team member") : t("provider_team.add_member_title", "New team member")}
          </div>
          <button onClick={onClose} className="text-muted hover:text-bone" data-testid="member-modal-close" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <form
          className="p-6 space-y-4"
          onSubmit={(e) => { e.preventDefault(); onSave(data, state.id); }}
          data-testid="member-form"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("provider_team.field_name", "Name")}>
              <input required value={data.name} onChange={(e) => set("name", e.target.value)} className="input-bare" data-testid="tm-field-name" maxLength={160} />
            </Field>
            <Field label={t("provider_team.field_role", "Role")}>
              <select value={data.role} onChange={(e) => set("role", e.target.value)} className="input-bare" data-testid="tm-field-role">
                {ROLES.map((r) => <option key={r} value={r}>{t(`provider_team.role_${r}`, r)}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("provider_team.field_email", "Email")}>
              <input type="email" value={data.email} onChange={(e) => set("email", e.target.value)} className="input-bare" data-testid="tm-field-email" />
            </Field>
            <Field label={t("provider_team.field_whatsapp", "WhatsApp")}>
              <input value={data.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+34 600 000 000" className="input-bare" data-testid="tm-field-whatsapp" />
            </Field>
          </div>
          <Field label={t("provider_team.field_telegram", "Telegram")}>
            <input value={data.telegram} onChange={(e) => set("telegram", e.target.value)} placeholder="@username" className="input-bare" data-testid="tm-field-telegram" />
          </Field>
          <Field label={t("provider_team.field_languages", "Languages spoken")}>
            <input value={data.languages} onChange={(e) => set("languages", e.target.value)} placeholder={t("provider_team.field_languages_ph", "en, es, fr")} className="input-bare" data-testid="tm-field-langs" />
          </Field>
          <Field label={t("provider_team.field_notes", "Internal notes")}>
            <textarea rows={3} value={data.notes} onChange={(e) => set("notes", e.target.value)} className="input-bare resize-y" data-testid="tm-field-notes" />
          </Field>
          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <input type="checkbox" checked={!!data.is_active} onChange={(e) => set("is_active", e.target.checked)} className="h-4 w-4 accent-teal" data-testid="tm-field-active" />
            <span>{t("provider_team.field_active", "Active")}</span>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost" data-testid="tm-cancel">{t("provider_team.cancel", "Cancel")}</button>
            <button type="submit" className="btn btn-primary" data-testid="tm-save">{t("provider_team.save", "Save")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RuleModal({ state, onClose, onSave, members, services, t }) {
  const [data, setData] = useState(state.data);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const isEdit = state.mode === "edit";
  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6 bg-ink-deep/70 backdrop-blur-sm" data-testid="rule-modal">
      <div className="w-full md:max-w-2xl max-h-[95svh] overflow-y-auto modal-panel">
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b hairline glass-solid z-10">
          <div className="font-serif-display text-xl">
            {isEdit ? t("provider_team.edit_member_title", "Edit rule") : t("provider_team.add_rule", "New rule")}
          </div>
          <button onClick={onClose} className="text-muted hover:text-bone" data-testid="rule-modal-close" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <form
          className="p-6 space-y-4"
          onSubmit={(e) => { e.preventDefault(); onSave(data, state.id); }}
          data-testid="rule-form"
        >
          <Field label={t("provider_team.rule_title_label", "Title")}>
            <input required value={data.title} onChange={(e) => set("title", e.target.value)} placeholder={t("provider_team.rule_title_ph", "Day-before cleaner reminder")} className="input-bare" data-testid="rule-field-title" maxLength={160} />
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("provider_team.rule_member", "Team member")}>
              <select required value={data.team_member_id} onChange={(e) => set("team_member_id", e.target.value)} className="input-bare" data-testid="rule-field-member">
                <option value="">—</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name} · {t(`provider_team.role_${m.role}`, m.role)}</option>)}
              </select>
            </Field>
            <Field label={t("provider_team.rule_service", "Service (optional)")}>
              <select value={data.service_id || ""} onChange={(e) => set("service_id", e.target.value)} className="input-bare" data-testid="rule-field-service">
                <option value="">{t("provider_team.rule_service_all", "All services")}</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label={t("provider_team.rule_trigger", "Trigger")}>
              <select value={data.trigger} onChange={(e) => set("trigger", e.target.value)} className="input-bare" data-testid="rule-field-trigger">
                {TRIGGERS.map((tr) => <option key={tr} value={tr}>{t(`provider_team.trigger_${tr}`, tr)}</option>)}
              </select>
            </Field>
            <Field label={t("provider_team.rule_offset", "When (minutes before)")}>
              <input type="number" min="0" step="1" value={data.offset_minutes} onChange={(e) => set("offset_minutes", Number(e.target.value))} className="input-bare" data-testid="rule-field-offset" />
              <span className="text-[11px] text-muted/70">{t("provider_team.rule_offset_hint", "e.g. 1440 = 24 hours before, 60 = 1 hour before")}</span>
            </Field>
            <Field label={t("provider_team.rule_channel", "Channel")}>
              <select value={data.channel} onChange={(e) => set("channel", e.target.value)} className="input-bare" data-testid="rule-field-channel">
                {CHANNELS.map((c) => <option key={c} value={c}>{t(`provider_team.channel_${c}`, c)}</option>)}
              </select>
            </Field>
          </div>
          <Field label={t("provider_team.rule_template", "Message template")}>
            <textarea required rows={4} value={data.template} onChange={(e) => set("template", e.target.value)} placeholder={t("provider_team.rule_template_ph", "Hi {{guest_name}}, tomorrow at 13:00 cleaning service required for {{service}}. Please confirm.")} className="input-bare resize-y" data-testid="rule-field-template" />
          </Field>
          <div className="flex flex-wrap gap-6 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!data.requires_confirmation} onChange={(e) => set("requires_confirmation", e.target.checked)} className="h-4 w-4 accent-teal" data-testid="rule-field-confirm" />
              <span>{t("provider_team.rule_requires_confirmation", "Requires confirmation reply")}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!data.is_active} onChange={(e) => set("is_active", e.target.checked)} className="h-4 w-4 accent-teal" data-testid="rule-field-active" />
              <span>{t("provider_team.rule_active", "Active")}</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost" data-testid="rule-cancel">{t("provider_team.cancel", "Cancel")}</button>
            <button type="submit" className="btn btn-primary" data-testid="rule-save">{t("provider_team.save", "Save")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-tiny block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
